import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import "./people.css";
import { listEmployees, listTraining, createTraining, listRequiredReading, assignSops } from "../../api/teamApi";
import { listSops } from "../../api/sopsApi";
import { describeApiError } from "../../api/client";
import { getUserRole } from "../../helpers/tenantHelpers";

/* Team → Training: required reading + every course completion across people.
   Required reading (crm_backend docs/TRAINING_SPEC.md Phase 1) assigns a
   PUBLISHED SOP to people — the assignment is onboarding signature steps, so
   acknowledging it records the version-pinned SOPAcknowledgment the person card,
   SOP coverage and the assistant already read. A draft can't be assigned, and
   someone already current on a version isn't re-nagged. Rows open the ONE
   employee card on its Training tab. */

const MANAGER_ROLES = new Set(["owner", "admin", "manager"]);
const fmtDate = (v) => (v ? new Date(v + (String(v).length === 10 ? "T00:00:00" : "")).toLocaleDateString() : "—");

export default function TeamTraining({ openEmployee }) {
  const role = String(getUserRole("Member") || "").toLowerCase();
  const isManager = MANAGER_ROLES.has(role);
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ employee_id: "", course: "", completed_on: "", hours: "", ceu_credits: "" });
  const [saving, setSaving] = useState(false);
  const [reading, setReading] = useState(null);          // GET /people/training/assignments/
  const [sops, setSops] = useState([]);                  // published SOPs, assignable
  const [assign, setAssign] = useState({ slugs: [], employee_ids: [], note: "", busy: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, t, rr, sl] = await Promise.all([listEmployees(), listTraining(), listRequiredReading(), listSops()]);
      setEmployees(e.data?.employees || []); setRecords(t.data?.training || []);
      setReading(rr.data || null);
      setSops((sl.data?.sops || []).filter((x) => x.is_published));
    } catch { toast.error("Couldn’t load training records."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const byEmployee = useMemo(() => {
    const m = new Map(employees.map((e) => [e.id, e]));
    const groups = new Map();
    records.forEach((r) => { const k = r.employee_id; if (!groups.has(k)) groups.set(k, []); groups.get(k).push(r); });
    return [...groups.entries()].map(([id, rows]) => ({ employee: m.get(id) || { id, name: `Employee #${id}` }, rows: rows.sort((a, b) => String(b.completed_on || "").localeCompare(String(a.completed_on || ""))) }))
      .sort((a, b) => String(a.employee.name || "").localeCompare(String(b.employee.name || "")));
  }, [employees, records]);

  const toggle = (key, value) => setAssign((a) => ({ ...a, [key]: a[key].includes(value) ? a[key].filter((x) => x !== value) : [...a[key], value] }));

  const sendAssignment = async (e) => {
    e.preventDefault();
    if (!assign.slugs.length) { toast.error("Pick at least one published SOP."); return; }
    if (!assign.employee_ids.length) { toast.error("Pick at least one person."); return; }
    setAssign((a) => ({ ...a, busy: true }));
    try {
      const r = await assignSops({ sop_slugs: assign.slugs, employee_ids: assign.employee_ids, note: assign.note });
      const rows = r.data?.assigned || [];
      const sent = rows.filter((x) => x.assignment_id).length;
      const skipped = rows.length - sent;
      toast.success(`Assigned to ${sent} ${sent === 1 ? "person" : "people"}${skipped ? ` · ${skipped} already current` : ""}`);
      setAssign({ slugs: [], employee_ids: [], note: "", busy: false });
      await load();
    } catch (ex) {
      toast.error(describeApiError(ex, { conflict: "Only a published SOP can be assigned — publish it first.",
        forbidden: "Only an admin or manager can assign training.", fallback: "Couldn’t assign that." }));
      setAssign((a) => ({ ...a, busy: false }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_id || !form.course.trim()) { toast.error("Pick a person and name the course."); return; }
    setSaving(true);
    try {
      await createTraining({ employee_id: Number(form.employee_id), course: form.course.trim(), completed_on: form.completed_on || null, hours: form.hours || null, ceu_credits: form.ceu_credits || 0 });
      toast.success("Recorded"); setForm({ employee_id: "", course: "", completed_on: "", hours: "", ceu_credits: "" }); await load();
    } catch (ex) { toast.error(ex?.payload?.detail || ex?.payload?.error || (ex?.status === 403 ? "Only admins and managers can record training." : "Couldn’t record it.")); }
    finally { setSaving(false); }
  };

  return (
    <div className="team-tab">
      <div className="ppl-grid">
        <section className="ppl-card ppl-card--wide">
          <h3>Required reading</h3>
          {!reading ? <div className="ppl-muted">Loading…</div> : (
            <>
              {reading.sops?.length ? (
                <div className="ppl-list">
                  {reading.sops.map((s) => (
                    <div key={s.slug} className="ppl-list-row">
                      <div className="ppl-list-main">
                        <span>{s.title}</span>
                        <span className="ppl-sub">v{s.version} · {s.acknowledged} of {reading.roster} acknowledged{s.outstanding ? ` · ${s.outstanding} outstanding` : ""}</span>
                      </div>
                      <span className={`ppl-pill ${s.outstanding ? "pending" : s.acknowledged ? "approved" : ""}`}>{s.acknowledged}/{reading.roster}</span>
                    </div>
                  ))}
                </div>
              ) : <div className="ppl-empty"><strong>No published SOPs yet.</strong><span>Publish one on Operations → SOPs and you can assign it as required reading.</span></div>}
              {reading.people?.some((p) => p.outstanding.length > 0) && (
                <div className="ppl-list">
                  <div className="ppl-sub" style={{ padding: "6px 0" }}>Outstanding</div>
                  {reading.people.filter((p) => p.outstanding.length).map((p) => (
                    <button key={p.employee_id} type="button" className="ppl-list-row ppl-list-row--btn" onClick={() => openEmployee(p.user_id, "training")}>
                      <div className="ppl-list-main">
                        <span className="ppl-name">{p.name}</span>
                        <span className="ppl-sub">{p.outstanding.map((o) => o.title + (o.needs_reack ? " (updated — needs re-acknowledging)" : "")).join(" · ")}</span>
                      </div>
                      <span className="ppl-chev">›</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </section>

        {isManager && (
          <section className="ppl-card ppl-card--wide">
            <h3>Assign required reading</h3>
            <p className="ppl-sub">They get a link on their phone; acknowledging it is recorded against that exact version. Anyone already current on a version is skipped.</p>
            <form className="ppl-form" onSubmit={sendAssignment}>
              <div className="ppl-form--wide">
                <span className="ppl-sub">Published SOPs</span>
                <div className="ppl-chips">
                  {sops.length === 0 && <span className="ppl-muted">Nothing published yet.</span>}
                  {sops.map((s) => (
                    <label key={s.slug} className={`chip ${assign.slugs.includes(s.slug) ? "is-on" : ""}`}>
                      <input type="checkbox" checked={assign.slugs.includes(s.slug)} onChange={() => toggle("slugs", s.slug)} />{s.title}
                    </label>
                  ))}
                </div>
              </div>
              <div className="ppl-form--wide">
                <span className="ppl-sub">Who</span>
                <div className="ppl-chips">
                  {employees.map((e) => (
                    <label key={e.id} className={`chip ${assign.employee_ids.includes(e.id) ? "is-on" : ""}`}>
                      <input type="checkbox" checked={assign.employee_ids.includes(e.id)} onChange={() => toggle("employee_ids", e.id)} />{e.name}
                    </label>
                  ))}
                </div>
              </div>
              <label className="ppl-form--wide"><span>Note <em>optional</em></span><input type="text" value={assign.note} onChange={(e) => setAssign({ ...assign, note: e.target.value })} placeholder="e.g. Read before your first stop Monday." /></label>
              <div className="ppl-form--wide"><button type="submit" className="ops-btn" disabled={assign.busy}>{assign.busy ? "Assigning…" : "Assign and notify"}</button></div>
            </form>
          </section>
        )}

        <section className="ppl-card ppl-card--wide">
          <h3>Completions</h3>
          {loading ? <div className="ppl-muted">Loading…</div>
            : byEmployee.length === 0 ? <div className="ppl-empty"><strong>No training recorded yet.</strong><span>Record a course completion below. SOP acknowledgments show on each person’s card.</span></div>
            : byEmployee.map(({ employee, rows }) => (
              <div key={employee.id} className="ppl-list">
                <button type="button" className="ppl-list-row ppl-list-row--btn" onClick={() => openEmployee(employee.user_id, "training")}>
                  <div className="ppl-list-main"><span className="ppl-name">{employee.name}</span><span className="ppl-sub">{employee.role || ""} · {rows.length} course{rows.length === 1 ? "" : "s"}</span></div>
                  <span className="ppl-chev">›</span>
                </button>
                {rows.map((r) => (
                  <div key={r.id} className="ppl-list-row ppl-list-row--sub">
                    <div className="ppl-list-main"><span>{r.course}</span><span className="ppl-sub">{fmtDate(r.completed_on)}{r.hours ? ` · ${r.hours} h` : ""}{Number(r.ceu_credits) > 0 ? ` · ${r.ceu_credits} CEU` : ""}{r.credential_id ? " · counts toward a credential" : ""}</span></div>
                  </div>
                ))}
              </div>
            ))}
        </section>

        {isManager && (
          <section className="ppl-card ppl-card--wide">
            <h3>Record a completion</h3>
            <form className="ppl-form" onSubmit={submit}>
              <label><span>Who</span>
                <select value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}>
                  <option value="">Select…</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </label>
              <label><span>Course</span><input type="text" value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })} placeholder="e.g. FDACS CEU — Lawn & Ornamental" /></label>
              <label><span>Completed</span><input type="date" value={form.completed_on} onChange={(e) => setForm({ ...form, completed_on: e.target.value })} /></label>
              <label><span>Hours <em>optional</em></span><input type="number" min="0" step="0.5" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} /></label>
              <label><span>CEU credits <em>optional</em></span><input type="number" min="0" step="0.5" value={form.ceu_credits} onChange={(e) => setForm({ ...form, ceu_credits: e.target.value })} /></label>
              <div className="ppl-form--wide"><button type="submit" className="ops-btn" disabled={saving}>{saving ? "Saving…" : "Record"}</button></div>
            </form>
          </section>
        )}
      </div>
    </div>
  );
}
